/**
 * EmailTracking - Email Tracking Dashboard for Admin Portal
 * 
 * Tracks all emails sent by the system:
 * - Re-engagement emails (Day 3 nudge, Day 7 full pitch)
 * - Welcome emails (child account creation)
 * - Promo code emails
 * 
 * Data sources:
 * - DynamoDB: EduQ-ReEngagementEmails-prod (re-engagement sends)
 * - Cognito: User attributes for conversion tracking
 * - SES CloudWatch: Open/click/bounce metrics
 */

import { useState, useEffect, useCallback } from 'react';
import { fetchAuthSession } from 'aws-amplify/auth';
import { useAdminAuth } from '../auth/useAdminAuth';
import './EmailTracking.css';

import { API_ENDPOINTS } from '../config/api-endpoints';

const USER_MANAGEMENT_URL = API_ENDPOINTS.ADMIN.USER_MANAGEMENT;

const EmailTracking = () => {
  const { user: currentUser } = useAdminAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('re-engagement');
  const [showTestUsers, setShowTestUsers] = useState(false);
  
  // Re-engagement data
  const [allPendingUsers, setAllPendingUsers] = useState([]);
  const [stats, setStats] = useState({
    day3Eligible: 0,
    day7Eligible: 0,
    converted: 0,
    pending: 0,
    conversionRate: 0
  });

  // Fetch email tracking data
  const fetchEmailData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString();
      if (!token) throw new Error('Not authenticated');

      const response = await fetch(`${USER_MANAGEMENT_URL}/users?limit=200`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) throw new Error(`Failed to fetch: ${response.status}`);
      const data = await response.json();

      if (data.success && data.users) {
        const allUsers = data.users;
        
        // Users who haven't completed payment (potential re-engagement targets)
        const unpaidUsers = allUsers.filter(u => {
          const isChild = u.accountType === 'child' || u.accountType === 'family_child';
          // Use signupProgress to determine if payment is done
          const paymentDone = u.signupProgress?.details?.payment_completed === true;
          return !isChild && !paymentDone && u.status === 'CONFIRMED';
        });

        // Users who completed payment (for conversion tracking)
        const paidUsers = allUsers.filter(u => {
          return u.signupProgress?.details?.payment_completed === true;
        });

        // Calculate days since signup for each unpaid user
        const now = new Date();
        const enrichedUnpaid = unpaidUsers.map(u => {
          const created = new Date(u.createdAt);
          const daysSinceSignup = Math.floor((now - created) / (1000 * 60 * 60 * 24));
          const isTestUser = created.getFullYear() < 2026;
          return {
            ...u,
            daysSinceSignup,
            isTestUser,
            emailStatus: daysSinceSignup < 3 ? 'too_early' :
                         daysSinceSignup < 7 ? 'day3_eligible' :
                         'day7_eligible'
          };
        });

        setAllPendingUsers(enrichedUnpaid);

        // Stats exclude test users
        const realUsers = enrichedUnpaid.filter(u => !u.isTestUser);
        const realTotal = allUsers.filter(u => {
          const created = new Date(u.createdAt);
          return created.getFullYear() >= 2026;
        });
        const realPaid = paidUsers.filter(u => {
          const created = new Date(u.createdAt);
          return created.getFullYear() >= 2026;
        });

        setStats({
          day3Eligible: realUsers.filter(u => u.daysSinceSignup >= 3 && u.daysSinceSignup < 7).length,
          day7Eligible: realUsers.filter(u => u.daysSinceSignup >= 7).length,
          converted: realPaid.length,
          pending: realUsers.length,
          conversionRate: realTotal.length > 0 
            ? Math.round((realPaid.length / realTotal.length) * 100) 
            : 0
        });
      }
    } catch (err) {
      console.error('[EmailTracking] Error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchEmailData();
  }, [fetchEmailData]);

  // Filtered users based on test user toggle
  const displayUsers = showTestUsers 
    ? allPendingUsers 
    : allPendingUsers.filter(u => !u.isTestUser);

  // Sort by signup date descending (newest first)
  const sortedUsers = [...displayUsers].sort((a, b) => {
    const dateA = new Date(a.createdAt);
    const dateB = new Date(b.createdAt);
    return dateB - dateA;
  });

  // Format date for display
  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-US', { 
      month: 'short', day: 'numeric', year: 'numeric'
    });
  };

  // Get email status badge
  const getStatusBadge = (status) => {
    const badges = {
      'too_early': { label: 'Too Early (<3d)', className: 'badge--muted' },
      'day3_eligible': { label: 'Day 3 Eligible', className: 'badge--warning' },
      'day7_eligible': { label: 'Day 7 Eligible', className: 'badge--danger' },
      'day3_sent': { label: 'Day 3 Sent ✅', className: 'badge--info' },
      'day7_sent': { label: 'Day 7 Sent ✅', className: 'badge--info' },
      'converted': { label: 'Converted ✅', className: 'badge--success' }
    };
    const badge = badges[status] || { label: status, className: 'badge--muted' };
    return <span className={`email-badge ${badge.className}`}>{badge.label}</span>;
  };

  // Get signup progress badge
  const getProgressBadge = (signupProgress) => {
    if (!signupProgress) return <span className="email-badge badge--muted">Unknown</span>;
    const { stage, stageLabel, isComplete } = signupProgress;
    if (isComplete) return <span className="email-badge badge--success">Complete ✅</span>;
    const stageColors = {
      'account_created': 'badge--muted',
      'email_confirmed': 'badge--warning',
      'payment_completed': 'badge--info',
      'setup_completed': 'badge--info',
      'student_created': 'badge--info'
    };
    return <span className={`email-badge ${stageColors[stage] || 'badge--muted'}`}>{stageLabel || stage}</span>;
  };

  if (loading) {
    return (
      <div className="email-tracking">
        <div className="email-tracking__header">
          <h2>📧 Email Tracking</h2>
        </div>
        <div className="email-tracking__loading">
          <div className="skeleton-card" />
          <div className="skeleton-card" />
          <div className="skeleton-card" />
        </div>
      </div>
    );
  }

  return (
    <div className="email-tracking">
      {/* Header */}
      <div className="email-tracking__header">
        <h2>📧 Email Tracking</h2>
        <button className="email-tracking__refresh" onClick={fetchEmailData}>
          🔄 Refresh
        </button>
      </div>

      {/* Error */}
      {error && (
        <div className="email-tracking__error">
          <strong>Error:</strong> {error}
          <button onClick={fetchEmailData}>Retry</button>
        </div>
      )}

      {/* Stats Cards */}
      <div className="email-tracking__stats">
        <div className="stat-card">
          <div className="stat-card__value">{stats.pending}</div>
          <div className="stat-card__label">Pending Setup</div>
          <div className="stat-card__sub">Signed up, no plan selected</div>
        </div>
        <div className="stat-card stat-card--warning">
          <div className="stat-card__value">{stats.day3Eligible}</div>
          <div className="stat-card__label">Day 3 Eligible</div>
          <div className="stat-card__sub">Ready for nudge email</div>
        </div>
        <div className="stat-card stat-card--danger">
          <div className="stat-card__value">{stats.day7Eligible}</div>
          <div className="stat-card__label">Day 7 Eligible</div>
          <div className="stat-card__sub">Ready for full pitch</div>
        </div>
        <div className="stat-card stat-card--success">
          <div className="stat-card__value">{stats.converted}</div>
          <div className="stat-card__label">Converted</div>
          <div className="stat-card__sub">{stats.conversionRate}% conversion rate</div>
        </div>
      </div>

      {/* Tabs */}
      <div className="email-tracking__tabs">
        <button 
          className={`tab-btn ${activeTab === 're-engagement' ? 'tab-btn--active' : ''}`}
          onClick={() => setActiveTab('re-engagement')}
        >
          Re-Engagement ({displayUsers.length})
        </button>
        <button 
          className={`tab-btn ${activeTab === 'welcome' ? 'tab-btn--active' : ''}`}
          onClick={() => setActiveTab('welcome')}
        >
          Welcome Emails
        </button>
        <button 
          className={`tab-btn ${activeTab === 'all' ? 'tab-btn--active' : ''}`}
          onClick={() => setActiveTab('all')}
        >
          All Emails
        </button>
      </div>

      {/* Re-Engagement Tab */}
      {activeTab === 're-engagement' && (
        <>
          {/* Show test users toggle */}
          <div className="email-tracking__filter-bar">
            <label className="email-tracking__checkbox">
              <input 
                type="checkbox" 
                checked={showTestUsers} 
                onChange={(e) => setShowTestUsers(e.target.checked)} 
              />
              Show test users (pre-2026)
            </label>
          </div>

          <div className="email-tracking__table-wrap">
            <table className="email-tracking__table">
              <thead>
                <tr>
                  <th>User</th>
                  <th>Email</th>
                  <th>Signed Up</th>
                  <th>Days</th>
                  <th>Email Status</th>
                  <th>Signup Progress</th>
                  <th>Tier</th>
                  <th>Account Type</th>
                </tr>
              </thead>
              <tbody>
                {sortedUsers.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="email-tracking__empty">
                      No pending users — everyone has completed setup! 🎉
                    </td>
                  </tr>
                ) : (
                  sortedUsers.map(user => (
                    <tr key={user.userId || user.sub} className={user.isTestUser ? 'email-tracking__test-row' : ''}>
                      <td className="email-tracking__user">
                        <strong>{user.name || '—'}</strong>
                      </td>
                      <td className="email-tracking__email">{user.email}</td>
                      <td>{formatDate(user.createdAt)}</td>
                      <td className="email-tracking__days">
                        <span className={`days-badge ${
                          user.daysSinceSignup >= 7 ? 'days-badge--danger' :
                          user.daysSinceSignup >= 3 ? 'days-badge--warning' :
                          'days-badge--ok'
                        }`}>
                          {user.daysSinceSignup}d
                        </span>
                      </td>
                      <td>{getStatusBadge(user.emailStatus)}</td>
                      <td>{getProgressBadge(user.signupProgress)}</td>
                      <td>
                        <span className="email-badge badge--muted">{user.tier || 'free'}</span>
                      </td>
                      <td>
                        <span className="email-badge badge--muted">{user.accountType || 'individual'}</span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* Welcome Emails Tab */}
      {activeTab === 'welcome' && (
        <div className="email-tracking__placeholder">
          <div className="placeholder-icon">📬</div>
          <h3>Welcome Email Tracking</h3>
          <p>Welcome emails are sent automatically when a parent creates a child account via the Family Account Manager Lambda.</p>
          <p>Tracking data will appear here once SES events are collected via CloudWatch.</p>
        </div>
      )}

      {/* All Emails Tab */}
      {activeTab === 'all' && (
        <div className="email-tracking__placeholder">
          <div className="placeholder-icon">📊</div>
          <h3>All Email Activity</h3>
          <p>This view will show all email types (re-engagement, welcome, promo, system) once SES metrics accumulate.</p>
          <div className="placeholder-metrics">
            <div className="placeholder-metric">
              <span className="placeholder-metric__label">SES Config Set</span>
              <span className="placeholder-metric__value">eduq-email-tracking ✅</span>
            </div>
            <div className="placeholder-metric">
              <span className="placeholder-metric__label">CloudWatch Destination</span>
              <span className="placeholder-metric__value">cloudwatch-all-emails ✅</span>
            </div>
            <div className="placeholder-metric">
              <span className="placeholder-metric__label">DynamoDB Table</span>
              <span className="placeholder-metric__value">EduQ-ReEngagementEmails-prod ✅</span>
            </div>
            <div className="placeholder-metric">
              <span className="placeholder-metric__label">Lambdas Tracked</span>
              <span className="placeholder-metric__value">12 functions / 16 email sends ✅</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmailTracking;
